import { Coordinate, Diagnostic, LogRecord, UnitOfMeasure } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import L from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { type RouteStatusSeries } from "../hooks/useRouteLogRecords";
import { type RouteMapView, useRouteMapSettings } from "../hooks/useRouteMapSettings";
import { useRoutePlayback } from "../hooks/useRoutePlayback";
import { getLatestStatusValueBefore } from "../utils/getLatestStatusValueBefore";
import { buildPlaybackTimeline, playbackHeading, playbackPositionAt, playedPaths } from "../utils/playback";
import {
  BOOLEAN_DIAGNOSTIC_IDS,
  isLitTellTale,
  isTellTale,
  shortName,
  tellTaleIso,
  tellTaleLabel,
  valueLabels,
} from "../utils/pointDetails";
import { assignPointsToRanges, buildColoredRuns, findNearestPointIndex, getSpeedRange, UNKNOWN_DEVICE_KEY } from "../utils/routeGeometry";
import { speedToWeatherColor, WEATHER_COLORS } from "../utils/speedToWeatherColor";
import { MapOverlayBox } from "./MapOverlayBox";
import { PlaybackVehicleMarker } from "./PlaybackVehicleMarker";
import { PointTellTale } from "./PointTellTale";
import { type PointDetailOption, ROUTE_MAP_SETTINGS_WIDTH_PX, RouteMapSettings } from "./RouteMapSettings";
import { RoutePlaybackControls } from "./RoutePlaybackControls";
import { MAP_TILE_LAYERS } from "@/constants/MapTiles";
import { coordinateKey, useCoordinateAddresses } from "@/hooks/addresses/useCoordinateAddresses";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { useDisplayUnitOfMeasure } from "@/hooks/useDisplayUnitOfMeasure";
import { formatNumber } from "@/utilities/formatNumber";
import "leaflet/dist/leaflet.css";

interface Props {
  logRecords: LogRecord[];
  /** Every value the vehicles reported during the shown trips; the point details pick from these. */
  statusSeries?: RouteStatusSeries;
  monochrome: boolean;
  tripRanges?: { deviceId?: string; start: Date; stop: Date }[];
  /** deviceId -> display label, used for the per-vehicle color legend. */
  deviceLabels?: Record<string, string>;
  fitKey?: string;
  loading?: boolean;
  /** True while any LogRecord query is (re)fetching; the map waits for it before fitting a new selection. */
  fetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** The status values (rpm, ignition, ...) failed to load; the points are shown without them. */
  statusError?: unknown;
  onRetryStatus?: () => void;
  /** The status values are still loading; the points are shown without them until they arrive. */
  statusLoading?: boolean;
}

type RoutePoint = {
  latitude: number;
  longitude: number;
  speed: number;
  dateTime: Date;
  deviceId?: string;
};

/** Fixed, stable-order palette assigned per vehicle in monochrome mode so routes stay distinguishable. */
const VEHICLE_COLORS = [
  "#1e88e5",
  "#e53935",
  "#43a047",
  "#fb8c00",
  "#8e24aa",
  "#00897b",
  "#3949ab",
  "#d81b60",
  "#6d4c41",
  "#546e7a",
];

/** A click closer than this (screen pixels) to a route point selects it. */
const POINT_PICK_RADIUS_PX = 20;

const NO_FIT_YET = Symbol("no-fit-yet");

/** The film's vehicle and the stretch it has driven. */
const PLAYBACK_COLOR = "#0d47a1";
/** How often the address is looked up again while the film runs. */
const PLAYBACK_ADDRESS_INTERVAL_MS = 5000;

function FitToRoute({ points, fitKey, settled = true }: { points: RoutePoint[]; fitKey?: string; settled?: boolean }) {
  const map = useMap();
  const lastFitKey = useRef<string | undefined | typeof NO_FIT_YET>(NO_FIT_YET);

  const fitRoute = useCallback(() => {
    if (points.length === 0) return;

    const bounds = L.latLngBounds(points.map(point => [point.latitude, point.longitude] as [number, number]));
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);

  useEffect(() => {
    if (points.length === 0) {
      // The map stays up without a route, so picking the same trips again must still fit them.
      lastFitKey.current = NO_FIT_YET;
      return;
    }
    if (!settled) return;
    if (fitKey === lastFitKey.current) return;

    fitRoute();
    lastFitKey.current = fitKey;
  }, [fitKey, map, points, fitRoute, settled]);

  return null;
}

/**
 * One click handler for the whole map: selects the route point nearest to the click.
 *
 * Near a point the cursor turns into an arrow, so the reader sees where a click picks a point;
 * elsewhere it stays Leaflet's hand for dragging the map.
 */
function PickRoutePoint({ points, onPick }: { points: RoutePoint[]; onPick: (point: RoutePoint) => void }) {
  const frame = useRef<number | null>(null);
  const nearestIndex = (target: { x: number; y: number }) =>
    findNearestPointIndex(
      points,
      target,
      point => map.latLngToContainerPoint([point.latitude, point.longitude]),
      POINT_PICK_RADIUS_PX,
    );
  const setCursor = (cursor: string) => {
    map.getContainer().style.cursor = cursor;
  };

  const map = useMapEvents({
    click: event => {
      const index = nearestIndex(event.containerPoint);
      if (index >= 0) onPick(points[index]);
    },
    // Once per frame at most: a long route has thousands of points to measure against.
    mousemove: event => {
      if (frame.current !== null) return;
      const target = event.containerPoint;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        setCursor(nearestIndex(target) >= 0 ? "default" : "");
      });
    },
    mouseout: () => setCursor(""),
    dragstart: () => setCursor(""),
  });

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return null;
}

/** Keeps the film's vehicle in view: pans once it nears the edge, and leaves the zoom alone. */
function FollowPlayback({ latitude, longitude }: { latitude: number; longitude: number }) {
  const map = useMap();

  useEffect(() => {
    const position = L.latLng(latitude, longitude);
    if (!map.getBounds().pad(-0.15).contains(position)) {
      map.panTo(position, { animate: false });
    }
  }, [map, latitude, longitude]);

  return null;
}

/** Remembers where the reader left the map, so the next visit starts there instead of at Germany. */
function RememberView({ onChange }: { onChange: (view: RouteMapView) => void }) {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      onChange({
        latitude: Number(center.lat.toFixed(5)),
        longitude: Number(center.lng.toFixed(5)),
        zoom: map.getZoom(),
      });
    },
  });

  return null;
}

function InvalidateMapOnResize() {
  const map = useMap();

  useEffect(() => {
    const mapContainer = map.getContainer();
    if (!mapContainer) return;

    let frameId: number | null = null;

    const invalidateSize = () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        map.invalidateSize();
        frameId = null;
      });
    };

    invalidateSize();

    const observedElement = mapContainer.parentElement ?? mapContainer;
    const resizeObserver = new ResizeObserver(() => {
      invalidateSize();
    });

    resizeObserver.observe(observedElement);
    window.addEventListener("resize", invalidateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", invalidateSize);
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [map]);

  return null;
}

/** What Geotab's DisplayUnitOfMeasure carries; the client types it as a plain UnitOfMeasure. */
type DisplayUnit = { baseId?: string; factorFromBase?: number; offsetFromBase?: number; name?: string };

function unitIdOf(diagnostic: Diagnostic | undefined): string | undefined {
  const unit = diagnostic?.unitOfMeasure as UnitOfMeasure | string | undefined;
  return typeof unit === "string" ? unit : unit?.id;
}

export function TripRouteMap({
  logRecords,
  statusSeries = {},
  monochrome,
  tripRanges = [],
  deviceLabels = {},
  fitKey,
  loading,
  fetching,
  error,
  onRetry,
  statusError,
  onRetryStatus,
  statusLoading,
}: Props) {
  const { t, i18n } = useTranslation();
  const [selectedPoint, setSelectedPoint] = useState<RoutePoint | null>(null);
  const {
    tileId,
    setTileId,
    view,
    setView,
    pointDetailIds,
    setPointDetailIds,
    isReady: settingsReady,
  } = useRouteMapSettings();

  const reportedIds = useMemo(
    () => [...new Set(Object.values(statusSeries).flatMap(byDiagnostic => Object.keys(byDiagnostic)))],
    [statusSeries],
  );
  const { data: diagnostics = {}, isPending: diagnosticsPending } = useDiagnosticsByIds([
    ...reportedIds,
    ...pointDetailIds,
  ]);
  const { data: displayUnitData } = useDisplayUnitOfMeasure();
  const displayUnits = (displayUnitData ?? []) as unknown as DisplayUnit[];
  // The unit's own symbol: the display unit that shows it unconverted (km/h, %, rpm, ...).
  const unitSymbol = (diagnostic: Diagnostic | undefined) => {
    const unitId = unitIdOf(diagnostic);
    const display = displayUnits.find(
      unit => unit.baseId === unitId && unit.factorFromBase === 1 && !unit.offsetFromBase,
    );
    return display?.name && display.name !== "None" ? display.name : "";
  };
  const pointDetailOptions = useMemo<PointDetailOption[]>(() => {
    const option = (id: string): PointDetailOption => {
      const name = diagnostics[id]?.name;
      return { id, label: shortName(name, id), group: isTellTale(id, name) ? "tellTale" : "engine" };
    };
    return [...new Set([...reportedIds, ...pointDetailIds])]
      .map(option)
      .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label, i18n.language));
  }, [diagnostics, i18n.language, pointDetailIds, reportedIds]);
  const tileLayer = MAP_TILE_LAYERS.find(layer => layer.id === tileId) ?? MAP_TILE_LAYERS[0];

  // Numbered per vehicle (1, 2, 3, ...) so each trip's start marker shows its position within that vehicle's selection.
  const normalizedRanges = useMemo(() => {
    const numberByDevice = new Map<string, number>();

    return tripRanges
      .map(range => {
        const startMs = range.start.getTime();
        const stopMs = range.stop.getTime();

        if (!Number.isFinite(startMs) || !Number.isFinite(stopMs) || stopMs < startMs) {
          return null;
        }

        const deviceKey = range.deviceId ?? UNKNOWN_DEVICE_KEY;
        const number = (numberByDevice.get(deviceKey) ?? 0) + 1;
        numberByDevice.set(deviceKey, number);

        return {
          deviceId: range.deviceId,
          startMs,
          stopMs,
          number,
        };
      })
      .filter(range => range !== null);
  }, [tripRanges]);

  // Valid, time-ordered points; with trip ranges, each point is filed under its trip in one pass.
  const pointsByTrip = useMemo(() => {
    const hasTripIntervals = normalizedRanges.length > 0;

    const points: RoutePoint[] = [...logRecords]
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
      .map(point => ({
        dateTime: new Date(point.dateTime),
        latitude: Number(point.latitude),
        longitude: Number(point.longitude),
        speed: Number(point.speed ?? 0),
        deviceId: point.device?.id,
      }))
      .filter(point => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));

    return hasTripIntervals ? assignPointsToRanges(points, normalizedRanges) : [points];
  }, [logRecords, normalizedRanges]);

  const routePoints = useMemo(() => pointsByTrip.flat(), [pointsByTrip]);

  const speedRange = useMemo(() => getSpeedRange(routePoints), [routePoints]);

  // Stable per-vehicle color assignment (order of first appearance), shared by segments, markers and legend.
  const deviceOrder = useMemo(() => {
    const order: string[] = [];
    const seen = new Set<string>();
    const addId = (id?: string) => {
      const key = id ?? UNKNOWN_DEVICE_KEY;
      if (!seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    };

    normalizedRanges.forEach(range => addId(range.deviceId));
    routePoints.forEach(point => addId(point.deviceId));
    return order;
  }, [normalizedRanges, routePoints]);

  const colorByDevice = useMemo(() => {
    const map = new Map<string, string>();
    deviceOrder.forEach((id, index) => map.set(id, VEHICLE_COLORS[index % VEHICLE_COLORS.length]));
    return map;
  }, [deviceOrder]);

  // One polyline per trip (split further where the colour changes), never across trips or vehicles.
  const coloredRuns = useMemo(() => {
    let groups: { deviceId: string; points: RoutePoint[] }[];
    if (normalizedRanges.length > 0) {
      groups = pointsByTrip.map((points, index) => ({
        deviceId: normalizedRanges[index].deviceId ?? points[0]?.deviceId ?? UNKNOWN_DEVICE_KEY,
        points,
      }));
    } else {
      // No trip ranges known: one line per vehicle.
      const byDevice = new Map<string, RoutePoint[]>();
      for (const point of routePoints) {
        const key = point.deviceId ?? UNKNOWN_DEVICE_KEY;
        const list = byDevice.get(key);
        if (list) list.push(point);
        else byDevice.set(key, [point]);
      }
      groups = [...byDevice].map(([deviceId, points]) => ({ deviceId, points }));
    }
    return buildColoredRuns(groups, (deviceId, segmentSpeed) =>
      monochrome
        ? (colorByDevice.get(deviceId) ?? VEHICLE_COLORS[0])
        : speedToWeatherColor(segmentSpeed, speedRange.min, speedRange.max),
    );
  }, [colorByDevice, monochrome, normalizedRanges, pointsByTrip, routePoints, speedRange.max, speedRange.min]);

  // One numbered start/end marker pair per selected trip, so parallel trips stay distinguishable.
  const tripMarkers = useMemo(() => {
    if (routePoints.length === 0) return [];

    if (normalizedRanges.length === 0) {
      return [{
        key: "trip-0",
        number: 1,
        start: routePoints[0],
        end: routePoints[routePoints.length - 1],
      }];
    }

    return normalizedRanges.flatMap((range, index) => {
      const pointsInRange = pointsByTrip[index];
      if (!pointsInRange?.length) return [];
      return [{
        key: `${range.deviceId ?? UNKNOWN_DEVICE_KEY}-${range.number}`,
        number: range.number,
        start: pointsInRange[0],
        end: pointsInRange[pointsInRange.length - 1],
      }];
    });
  }, [normalizedRanges, pointsByTrip, routePoints]);

  const vehicleLegendEntries = useMemo(() => {
    return deviceOrder
      .filter(id => id !== UNKNOWN_DEVICE_KEY)
      .map(id => ({ id, color: colorByDevice.get(id) ?? VEHICLE_COLORS[0], label: deviceLabels[id] ?? id }));
  }, [colorByDevice, deviceLabels, deviceOrder]);

  // The film: trips one after another, each in real time times the chosen factor.
  const timeline = useMemo(() => buildPlaybackTimeline(pointsByTrip), [pointsByTrip]);
  const playback = useRoutePlayback(timeline.duration, fitKey);
  const playbackPosition = playback.time === null ? null : playbackPositionAt(timeline, playback.time);
  const playbackActive = playbackPosition !== null && playbackPosition.index >= 0;
  const shownPoint = playbackActive ? timeline.points[playbackPosition.index] : selectedPoint;
  const playedRoute = playbackActive ? playedPaths(timeline, playbackPosition) : [];

  const handlePick = useCallback(
    (point: RoutePoint) => {
      setSelectedPoint(point);
      // During the film a click jumps it to that point.
      if (playback.time !== null) {
        const index = timeline.points.indexOf(point);
        if (index >= 0) playback.seek(timeline.offsets[index]);
      }
    },
    [playback, timeline],
  );

  // While the film runs the address follows every few seconds rather than every point, which
  // would ask Geotab dozens of times a second.
  const [addressPoint, setAddressPoint] = useState<RoutePoint | null>(null);
  const lastAddressAt = useRef(0);
  useEffect(() => {
    if (playback.direction !== 0 && Date.now() - lastAddressAt.current < PLAYBACK_ADDRESS_INTERVAL_MS) return;
    lastAddressAt.current = Date.now();
    setAddressPoint(shownPoint);
  }, [shownPoint, playback.direction]);

  // One point at a time, so one address call per lookup; the lookup keeps what it already resolved.
  const addressCoordinates = useMemo(() => {
    const coordinates = new Map<string, Coordinate>();
    if (addressPoint) {
      const coordinate = { x: addressPoint.longitude, y: addressPoint.latitude };
      coordinates.set(coordinateKey(coordinate) as string, coordinate);
    }
    return coordinates;
  }, [addressPoint]);
  const { data: addresses, isFetching: addressLoading, isError: addressError } =
    useCoordinateAddresses(addressCoordinates);
  const selectedAddress = addressPoint
    ? addresses?.get(coordinateKey({ x: addressPoint.longitude, y: addressPoint.latitude }) as string)
    : undefined;

  useEffect(() => {
    if (!selectedPoint) return;

    const hasSelectedPoint = routePoints.some(
      point =>
        point.dateTime.getTime() === selectedPoint.dateTime.getTime() &&
        point.latitude === selectedPoint.latitude &&
        point.longitude === selectedPoint.longitude,
    );

    if (!hasSelectedPoint) {
      setSelectedPoint(null);
    }
  }, [routePoints, selectedPoint]);

  // Waits for the stored view: the map only reads its starting point once.
  if (!settingsReady) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", width: "100%", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasRoute = routePoints.length >= 2;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        // Leaflet shows a pointing hand over the drawn route and markers; an arrow there makes
        // the point under the cursor easier to aim at.
        "& .leaflet-interactive": { cursor: "default" },
      }}
    >
      <MapContainer
        center={[view.latitude, view.longitude]}
        zoom={view.zoom}
        preferCanvas
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer key={tileLayer.id} url={tileLayer.url} attribution={tileLayer.attribution} maxZoom={tileLayer.maxZoom} />
        <InvalidateMapOnResize />
        {/* Not during the film: following the vehicle pans the map every few seconds, and each
            pan would be written to Geotab as the reader's view. */}
        {!playbackActive && <RememberView onChange={setView} />}
        <FitToRoute points={routePoints} fitKey={fitKey} settled={!fetching} />

        <PickRoutePoint points={routePoints} onPick={handlePick} />

        {coloredRuns.map((run, index) => (
          <Polyline
            key={`${run.deviceId}-${index}`}
            positions={run.positions}
            // Faded during the film, so the stretch already driven stands out.
            pathOptions={{ color: run.color, weight: 5, opacity: playbackActive ? 0.35 : 0.9, interactive: false }}
          />
        ))}

        {playbackActive && (
          <>
            <Polyline
              positions={playedRoute}
              pathOptions={{ color: PLAYBACK_COLOR, weight: 6, opacity: 0.9, interactive: false }}
            />
            <FollowPlayback latitude={playbackPosition.latitude} longitude={playbackPosition.longitude} />
          </>
        )}

        {tripMarkers.map(marker => (
          <CircleMarker
            key={`trip-start-${marker.key}`}
            center={[marker.start.latitude, marker.start.longitude]}
            radius={9}
            pathOptions={{ color: "#2e7d32", fillColor: "#2e7d32", fillOpacity: 1 }}
          >
            <Tooltip permanent direction="top" offset={[0, -6]} opacity={1}>
              {marker.number}
            </Tooltip>
          </CircleMarker>
        ))}
        {tripMarkers.map(marker => (
          <CircleMarker
            key={`trip-end-${marker.key}`}
            center={[marker.end.latitude, marker.end.longitude]}
            radius={6}
            pathOptions={{ color: "#c62828", fillColor: "#c62828", fillOpacity: 1 }}
          />
        ))}

        {/* Last, so the vehicle is drawn over the start and end markers it passes. */}
        {playbackActive && shownPoint && (
          <PlaybackVehicleMarker
            latitude={playbackPosition.latitude}
            longitude={playbackPosition.longitude}
            heading={playbackHeading(timeline, playbackPosition.index)}
            stopped={shownPoint.speed < 1}
            color={PLAYBACK_COLOR}
          />
        )}
      </MapContainer>

      {hasRoute && (
        <RoutePlaybackControls
          playback={playback}
          duration={timeline.duration}
          currentTimeLabel={shownPoint ? formatDate(shownPoint.dateTime, i18n.language, { seconds: true }) : undefined}
        />
      )}

      <RouteMapSettings
        tileId={tileLayer.id}
        onTileChange={setTileId}
        pointDetailOptions={pointDetailOptions}
        pointDetailIds={pointDetailIds}
        onPointDetailIdsChange={setPointDetailIds}
        pointDetailsLoading={statusLoading || diagnosticsPending}
      />

      {!hasRoute && (
        <MapOverlayBox sx={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : error ? (
            <>
              <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                {t("route.loadError")}
              </Typography>
              <Button variant="outlined" color="error" size="small" onClick={onRetry} sx={{ pointerEvents: "auto" }}>
                {t("route.retry")}
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {tripRanges.length === 0 ? t("route.selectTrip") : t("route.noRouteData")}
            </Typography>
          )}
        </MapOverlayBox>
      )}

      {hasRoute && !monochrome && (
        <MapOverlayBox sx={{ right: 16 + ROUTE_MAP_SETTINGS_WIDTH_PX, top: 16, minWidth: 220 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            {t("route.speedLegend")}
          </Typography>
          <Box
            sx={{
              height: 8,
              borderRadius: 1,
              background: `linear-gradient(to right, ${WEATHER_COLORS.join(",")})`,
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Typography variant="caption">{t("route.speedValue", { value: Math.round(speedRange.min) })}</Typography>
            <Typography variant="caption">{t("route.speedValue", { value: Math.round(speedRange.max) })}</Typography>
          </Box>
        </MapOverlayBox>
      )}

      {hasRoute && monochrome && vehicleLegendEntries.length > 1 && (
        <MapOverlayBox sx={{ right: 16 + ROUTE_MAP_SETTINGS_WIDTH_PX, top: 16, minWidth: 180 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
            {t("route.vehicleLegend")}
          </Typography>
          {vehicleLegendEntries.map(entry => (
            <Box key={entry.id} sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: entry.color, flexShrink: 0 }} />
              <Typography variant="caption" noWrap>{entry.label}</Typography>
            </Box>
          ))}
        </MapOverlayBox>
      )}

      {/* Below Leaflet's zoom buttons, which take the top left corner. */}
      {hasRoute && error ? (
        <MapOverlayBox sx={{ left: 16, top: 96, maxWidth: 360 }}>
          <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
            {t("route.partialLoadError")}
          </Typography>
          <Button size="small" color="error" onClick={onRetry} sx={{ pointerEvents: "auto" }}>
            {t("route.retry")}
          </Button>
        </MapOverlayBox>
      ) : null}

      {hasRoute && (
        <MapOverlayBox sx={{ left: 16, bottom: 16, minWidth: 260 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
            {t("route.pointDetails")}
          </Typography>

          {statusError ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Typography variant="caption" color="error">
                {t("route.statusLoadError")}
              </Typography>
              <Button size="small" color="error" onClick={onRetryStatus} sx={{ pointerEvents: "auto" }}>
                {t("route.retry")}
              </Button>
            </Box>
          ) : statusLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                {t("route.statusLoading")}
              </Typography>
            </Box>
          ) : null}

          {shownPoint ? (
            <>
              <Typography variant="caption" sx={{ display: "block" }}>
                {t("route.time")}: {formatDate(shownPoint.dateTime, i18n.language, { seconds: true })}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                {t("route.location")}: {shownPoint.latitude.toFixed(5)}, {shownPoint.longitude.toFixed(5)}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                {t("route.address")}:{" "}
                {selectedAddress
                  ? selectedAddress
                  : addressLoading
                    ? t("route.addressLoading")
                    : addressError
                      ? t("route.addressError")
                      : t("route.addressNone")}
              </Typography>

              {pointDetailIds.map(id => {
                const entries = shownPoint.deviceId ? statusSeries[shownPoint.deviceId]?.[id] : undefined;
                const diagnostic = diagnostics[id];
                const tellTale = isTellTale(id, diagnostic?.name);
                // Geotab reports a lamp only when it changes, and 0 when it goes out (schiwy: lit for
                // 18 minutes without a row in between) — so its last value holds until then, however old.
                const value = entries
                  ? getLatestStatusValueBefore(entries, shownPoint.dateTime.getTime(), tellTale ? Infinity : undefined)
                  : null;
                if (value == null) return null;
                const meaning = valueLabels(diagnostic?.name)?.get(value);
                if (tellTale) {
                  return isLitTellTale(value) ? (
                    <PointTellTale
                      key={id}
                      iso={tellTaleIso(id)}
                      status={value}
                      label={tellTaleLabel(diagnostic?.name, id)}
                    />
                  ) : null;
                }
                const shown = BOOLEAN_DIAGNOSTIC_IDS.has(id)
                  ? value
                    ? t("yes")
                    : t("no")
                  : (meaning ??
                    `${formatNumber(value, i18n.language, { precision: Number.isInteger(value) ? 0 : 2 })} ${unitSymbol(diagnostic)}`.trim());
                return (
                  <Typography key={id} variant="caption" sx={{ display: "block" }}>
                    {shortName(diagnostic?.name, id)}: {shown}
                  </Typography>
                );
              })}
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t("route.selectMapPoint")}
            </Typography>
          )}
        </MapOverlayBox>
      )}
    </Box>
  );
}
